import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  MoveDiagonalIcon,
  PlusIcon
} from 'lucide-react';
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { CreateAccountDialog } from './dialogs/CreateAccountDialog';

import { EditDynamicSelectField } from '@/components/EditDynamicSelectField';
import { EditHrEmployeeSelectField } from '@/components/EditHrEmployeeSelectField';
import { Button } from '@/components/ui/button';
import { Empty, EmptyContent } from '@/components/ui/empty';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '@/components/ui/pagination';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { type ColumnConfig } from '@/components/views/ColumnSelector';
import { DEFAULT_ACCOUNT_COLUMNS } from '@/components/views/defaultColumns';
import { TableCellRenderer } from '@/components/views/TableCellRenderer';
import { useColumnSelectionLabels } from '@/hooks/useColumnSelectionLabels';
import { useDebounce } from '@/hooks/useDebounce';

interface Account {
  city?: string;
  description?: string;
  email?: string;
  id: string;
  industryName?: string;
  investorCategory?: string;
  name: string;
}

interface AccountEdge {
  node: Account;
}

interface GetAccountsData {
  accounts: {
    edges: AccountEdge[];
    pageInfo: PageInfo;
    totalCount: number;
  };
}

interface GetSavedViewData {
  savedView: SavedView;
}

interface PageInfo {
  endCursor?: string;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor?: string;
}

interface SavedView {
  columnsConfig?: string;
  filterJson: string;
  id: string;
  name: string;
  targetModel: string;
}

const SORTABLE_ACCOUNT_FIELDS: Record<string, string> = {
  city: 'city',
  description: 'description',
  email: 'email',
  industryName: 'industry_id',
  investorCategory: 'investor_category',
  name: 'name'
};

type SortDirection = 'asc' | 'desc';
const ACCOUNT_COLUMNS_STORAGE_PREFIX = 'crm-accounts-column-widths:';
const DEFAULT_ACCOUNT_COLUMN_WIDTH = 300;
const MIN_ACCOUNT_COLUMN_WIDTH = 120;
const MAX_ACCOUNT_COLUMN_WIDTH = 900;

// Build dynamic GraphQL query based on selected columns
function buildAccountsQuery(columns: ColumnConfig[]) {
  // Filter out 'id' since it's always included, and filter out 'cursor' since it's not part of the node
  const filteredColumns = columns.filter(
    col => col.field !== 'id' && col.field !== 'cursor'
  );
  const fields = filteredColumns.map(col => col.field).join('\n          ');

  return gql`
    query GetAccounts(
      $name: String
      $first: Int
      $after: String
      $domain: String
      $order: String
      $ownerId: ID
      $relationshipType: String
      $relationshipPriority: String
    ) {
      accounts(
        name: $name
        first: $first
        after: $after
        domain: $domain
        order: $order
        ownerId: $ownerId
        relationshipType: $relationshipType
        relationshipPriority: $relationshipPriority
      ) {
        edges {
          node {
            id
            ${fields}
          }
          cursor
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        totalCount
      }
    }
  `;
}

function getAccountsOrder(
  sortField: null | string,
  sortDirection: SortDirection
) {
  if (!sortField) {
    return 'name asc';
  }

  const backendField = SORTABLE_ACCOUNT_FIELDS[sortField];
  if (!backendField) {
    return 'name asc';
  }

  return `${backendField} ${sortDirection}`;
}

function readColumnWidths(storageKey: string): Record<string, number> {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue) as Record<string, unknown>;
    const nextValue: Record<string, number> = {};

    for (const [field, value] of Object.entries(parsedValue)) {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        continue;
      }

      nextValue[field] = Math.max(
        MIN_ACCOUNT_COLUMN_WIDTH,
        Math.min(MAX_ACCOUNT_COLUMN_WIDTH, value)
      );
    }

    return nextValue;
  } catch {
    return {};
  }
}

function writeColumnWidths(
  storageKey: string,
  columnWidths: Record<string, number>
) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(columnWidths));
  } catch {
    // Ignore storage failures to avoid blocking table rendering.
  }
}

const GET_ACCOUNTS = gql`
  query GetAccounts(
    $name: String
    $first: Int
    $after: String
    $domain: String
    $order: String
    $ownerId: ID
    $relationshipType: String
    $relationshipPriority: String
  ) {
    accounts(
      name: $name
      first: $first
      after: $after
      domain: $domain
      order: $order
      ownerId: $ownerId
      relationshipType: $relationshipType
      relationshipPriority: $relationshipPriority
    ) {
      edges {
        node {
          id
          name
          email
          city
          investorCategory
          industryName
          description
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

const GET_SAVED_VIEW = gql`
  query GetSavedView($id: ID!) {
    savedView(id: $id) {
      id
      name
      filterJson
      columnsConfig
      targetModel
    }
  }
`;

const ITEMS_PER_PAGE = 20;

export default function AccountsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewId = searchParams.get('viewId');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Fetch the saved view if viewId is provided
  const { data: viewData } = useQuery<GetSavedViewData>(GET_SAVED_VIEW, {
    skip: !viewId,
    variables: { id: viewId }
  });

  // Redirect if the view is for contacts, not accounts
  useEffect(() => {
    if (viewData?.savedView.targetModel === 'contact') {
      void navigate(`/firm/crm-contacts?viewId=${viewId ?? ''}`, {
        replace: true
      });
    }
  }, [viewData, viewId, navigate]);

  // Don't render if we need to redirect
  if (viewData?.savedView.targetModel === 'contact') {
    return null;
  }

  return (
    <>
      <AccountsTable
        setIsCreateDialogOpen={setIsCreateDialogOpen}
        viewData={viewData}
      />
      <CreateAccountDialog
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        refetchQuery={{
          query: GET_ACCOUNTS,
          variables: { first: ITEMS_PER_PAGE }
        }}
      />
    </>
  );
}

function AccountsTable({
  setIsCreateDialogOpen,
  viewData: propViewData
}: {
  setIsCreateDialogOpen: Dispatch<SetStateAction<boolean>>;
  viewData?: GetSavedViewData | undefined;
}) {
  const [searchParams] = useSearchParams();
  const viewId = searchParams.get('viewId');
  const columnWidthsStorageKey = `${ACCOUNT_COLUMNS_STORAGE_PREFIX}${viewId ?? 'default'}`;

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [sortField, setSortField] = useState<null | string>('name');
  const [currentPage, setCurrentPage] = useState(1);
  // Map of page number -> cursor for that page (page 1 has no cursor/empty string)
  const [cursorMap, setCursorMap] = useState<Map<number, string>>(
    () => new Map([[1, '']])
  );

  // Filter states
  const [ownerFilter, setOwnerFilter] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [relationshipTypeFilter, setRelationshipTypeFilter] = useState('');
  const [relationshipPriorityFilter, setRelationshipPriorityFilter] =
    useState('');

  // Helper to reset pagination
  const resetPagination = () => {
    setCurrentPage(1);
    setCursorMap(new Map([[1, '']]));
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchTerm);
      setCurrentPage(1);
      setCursorMap(new Map([[1, '']]));
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Use prop viewData if available, otherwise fetch
  const { data: fetchedViewData, loading: viewLoading } =
    useQuery<GetSavedViewData>(GET_SAVED_VIEW, {
      skip: !viewId || !!propViewData,
      variables: { id: viewId }
    });

  const viewData = propViewData || fetchedViewData;
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const resizingFieldRef = useRef<null | string>(null);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(DEFAULT_ACCOUNT_COLUMN_WIDTH);

  // Parse columns config from saved view or use defaults
  const columns: ColumnConfig[] = React.useMemo(() => {
    if (viewData?.savedView?.columnsConfig) {
      try {
        const config = JSON.parse(viewData.savedView.columnsConfig);
        return config.selectedColumns || DEFAULT_ACCOUNT_COLUMNS;
      } catch (e) {
        return DEFAULT_ACCOUNT_COLUMNS;
      }
    }
    return DEFAULT_ACCOUNT_COLUMNS;
  }, [viewData]);

  useEffect(() => {
    setColumnWidths(readColumnWidths(columnWidthsStorageKey));
  }, [columnWidthsStorageKey]);

  useEffect(() => {
    writeColumnWidths(columnWidthsStorageKey, columnWidths);
  }, [columnWidths, columnWidthsStorageKey]);

  const getColumnWidth = useCallback(
    (field: string) => columnWidths[field] ?? DEFAULT_ACCOUNT_COLUMN_WIDTH,
    [columnWidths]
  );

  const handleColumnResizeStart = useCallback(
    (field: string, event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      resizingFieldRef.current = field;
      resizeStartXRef.current = event.clientX;
      resizeStartWidthRef.current = getColumnWidth(field);
    },
    [getColumnWidth]
  );

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const resizingField = resizingFieldRef.current;
      if (!resizingField) {
        return;
      }

      const deltaX = event.clientX - resizeStartXRef.current;
      const nextWidth = Math.max(
        MIN_ACCOUNT_COLUMN_WIDTH,
        Math.min(MAX_ACCOUNT_COLUMN_WIDTH, resizeStartWidthRef.current + deltaX)
      );

      setColumnWidths(prev => ({
        ...prev,
        [resizingField]: nextWidth
      }));
    };

    const handleMouseUp = () => {
      resizingFieldRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Build dynamic query based on columns
  const ACCOUNTS_QUERY = React.useMemo(
    () => buildAccountsQuery(columns),
    [columns]
  );

  // Fetch selection labels for columns
  const selectionLabels = useColumnSelectionLabels('account', columns);

  const currentCursor = cursorMap.get(currentPage) ?? '';

  // Wait for view to load before fetching accounts if a viewId is provided
  const shouldSkipAccountsQuery = viewId ? viewLoading : false;

  const { data, error, loading, previousData } = useQuery<GetAccountsData>(
    ACCOUNTS_QUERY,
    {
      skip: shouldSkipAccountsQuery,
      variables: {
        after: currentCursor || undefined,
        domain: viewData?.savedView.filterJson ?? undefined,
        first: ITEMS_PER_PAGE,
        name: debouncedSearch || undefined,
        order: getAccountsOrder(sortField, sortDirection),
        ownerId: ownerFilter?.id || undefined,
        relationshipPriority: relationshipPriorityFilter || undefined,
        relationshipType: relationshipTypeFilter || undefined
      }
    }
  );
  const accountsData = data ?? previousData;

  // Use `data` (not `accountsData`) to avoid stale cursors from `previousData`
  const endCursor = data?.accounts.pageInfo.endCursor;
  const hasNextPage = data?.accounts.pageInfo.hasNextPage ?? false;

  if (endCursor && hasNextPage && !cursorMap.has(currentPage + 1)) {
    setCursorMap(prev => new Map(prev).set(currentPage + 1, endCursor));
  }

  const handleNextPage = () => {
    if (hasNextPage && cursorMap.has(currentPage + 1)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (page: number) => {
    if (cursorMap.has(page)) {
      setCurrentPage(page);
    }
  };

  const accounts = accountsData?.accounts.edges.map(edge => edge.node) ?? [];
  const totalCount = accountsData?.accounts.totalCount ?? 0;
  const hasPreviousPage = currentPage > 1;
  // Use accountsData for UI state so the button stays visible during loading
  const canGoNext =
    (accountsData?.accounts.pageInfo.hasNextPage ?? false) &&
    cursorMap.has(currentPage + 1);

  // Calculate total pages (approximate for cursor-based pagination)
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + accounts.length;

  const handleSort = (field: string) => {
    if (!(field in SORTABLE_ACCOUNT_FIELDS)) {
      return;
    }

    setCurrentPage(1);
    setCursorMap(new Map([[1, '']]));

    if (sortField === field) {
      setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: ('ellipsis' | number)[] = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="flex min-h-0 grow flex-col">
      {/* Filter/Action Bar */}
      <div className="bg-sidebar flex justify-between gap-x-2.5 overflow-y-hidden border-b px-5 py-1.5 md:pl-14">
        <div className="flex items-end gap-x-2.5">
          <div className="w-[187px]">
            <Field className="gap-0">
              <FieldLabel
                className="h-5"
                htmlFor="search-field"
              >
                Search
              </FieldLabel>
              <Input
                id="search-field"
                onChange={e => {
                  setSearchTerm(e.target.value);
                }}
                placeholder="Search accounts by name..."
                value={searchTerm}
              />
            </Field>
          </div>
          <div className="w-[187px]">
            <EditHrEmployeeSelectField
              fieldClassName="gap-0"
              label="Owner"
              labelClassName="h-5"
              onChange={(id, name) => {
                setOwnerFilter(id ? { id, name } : null);
                resetPagination();
              }}
              placeholder="Filter by owner"
              value={ownerFilter}
            />
          </div>
          <div className="w-[187px]">
            <EditDynamicSelectField
              fieldClassName="gap-0"
              fieldName="relationship_type"
              label="Relationship Type"
              labelClassName="h-5"
              model="opstream.account"
              onChange={v => {
                setRelationshipTypeFilter(v);
                resetPagination();
              }}
              placeholder="Filter by type"
              value={relationshipTypeFilter}
            />
          </div>
          <div className="w-[187px]">
            <EditDynamicSelectField
              fieldClassName="gap-0"
              fieldName="relationship_priority"
              label="Relationship Priority"
              labelClassName="h-5"
              model="opstream.account"
              onChange={v => {
                setRelationshipPriorityFilter(v);
                resetPagination();
              }}
              placeholder="Filter by priority"
              value={relationshipPriorityFilter}
            />
          </div>
        </div>
        <div className="flex items-center gap-x-2.5">
          <Button
            onClick={() => {
              setIsCreateDialogOpen(true);
            }}
          >
            <PlusIcon />
            Create Account
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex min-h-0 flex-1 flex-col px-5 py-5 md:pl-14">
        {loading && !accountsData?.accounts ? (
          <div className="flex min-h-[60vh] w-full items-center justify-center">
            <Spinner className="size-8 text-blue-500" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6">
            <h3 className="font-semibold text-red-800">
              Error loading accounts
            </h3>
            <p className="mt-2 text-sm text-red-700">{error.message}</p>
          </div>
        ) : (
          <div className="grid h-full max-h-full grid-rows-[1fr_auto]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-0" />
                  {columns.map(col => (
                    <TableHead
                      className="relative text-xs font-semibold"
                      key={col.field}
                      style={{
                        maxWidth: getColumnWidth(col.field),
                        minWidth: getColumnWidth(col.field),
                        width: getColumnWidth(col.field)
                      }}
                    >
                      {col.field in SORTABLE_ACCOUNT_FIELDS ? (
                        <button
                          className="flex cursor-pointer items-center gap-1"
                          onClick={() => {
                            handleSort(col.field);
                          }}
                          type="button"
                        >
                          <span>{col.label}</span>
                          {sortField === col.field ? (
                            sortDirection === 'asc' ? (
                              <ArrowUpIcon className="text-muted-foreground size-3.5" />
                            ) : (
                              <ArrowDownIcon className="text-muted-foreground size-3.5" />
                            )
                          ) : (
                            <ArrowUpDownIcon className="text-muted-foreground size-3.5" />
                          )}
                        </button>
                      ) : (
                        col.label
                      )}
                      <div
                        className="bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100"
                        onMouseDown={event => {
                          handleColumnResizeStart(col.field, event);
                        }}
                      />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      className="p-0"
                      colSpan={columns.length + 1}
                    >
                      <Empty className="px-0! pt-5! pb-0!">
                        <EmptyContent>
                          <Button
                            onClick={() => {
                              setIsCreateDialogOpen(true);
                            }}
                          >
                            <PlusIcon />
                            Create Account
                          </Button>
                        </EmptyContent>
                      </Empty>
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map(account => (
                    <TableRow key={account.id}>
                      <TableCell className="w-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              asChild
                              size="icon"
                              variant="ghost"
                            >
                              <Link to={account.id}>
                                <MoveDiagonalIcon />
                              </Link>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Account Details</TooltipContent>
                        </Tooltip>
                      </TableCell>
                      {columns.map(col => (
                        <TableCell
                          className={`max-w-[300px] overflow-hidden ${col.field === 'name' ? 'font-medium' : ''}`}
                          key={col.field}
                          style={{
                            maxWidth: getColumnWidth(col.field),
                            minWidth: getColumnWidth(col.field),
                            width: getColumnWidth(col.field)
                          }}
                        >
                          <TableCellRenderer
                            column={col}
                            record={account}
                            selectionLabels={selectionLabels}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalCount > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-between pt-5">
                <div className="text-sm">
                  Showing {startIndex + 1} to {Math.min(endIndex, totalCount)}{' '}
                  of {totalCount} accounts
                </div>
                <Pagination className="mx-0 w-auto">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        className="cursor-pointer"
                        onClick={e => {
                          e.preventDefault();
                          if (hasPreviousPage) handlePreviousPage();
                        }}
                      />
                    </PaginationItem>

                    {getPageNumbers().map((page, idx) => {
                      if (page === 'ellipsis') {
                        return (
                          <PaginationItem key={`ellipsis-${String(idx)}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      const canNavigate = cursorMap.has(page);
                      return (
                        <PaginationItem key={page}>
                          <PaginationLink
                            className="cursor-pointer"
                            isActive={currentPage === page}
                            onClick={e => {
                              e.preventDefault();
                              if (canNavigate) handlePageClick(page);
                            }}
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}

                    <PaginationItem>
                      <PaginationNext
                        className="cursor-pointer"
                        onClick={e => {
                          e.preventDefault();
                          if (canGoNext) handleNextPage();
                        }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
